import i18nFactory from '@yandex-int/i18n';
import { compact } from 'lodash';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';

import { HintItem } from '../../components/PhrasesSelectorWizard/PhrasesSelectorWizard';
import { getKeywordStatPhrases } from '../../service/PhrasesSelectorWizard/PhrasesSelectorWizard';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import { CampaignType } from '../../typings/domain';
import {
    PhrasesSelectorInputColumns,
    PhrasesSelectorListItem,
    PhrasesSelectorWizardId,
    PhrasesSelectorWizardState
} from '../../typings/phrases';
import { Regions, State } from '../../typings/state';

import * as keyset from './phrasesSelectorWizard.i18n';

const i18n = i18nFactory(keyset);

export const getPhraseWizardCacheKey = (state: State, wizardId: PhrasesSelectorWizardId): PhrasesSelectorWizardId => {
    return wizardId;
};

export function selectPhrasesSelectorWizard(
    state: State,
    wizardId: PhrasesSelectorWizardId
): PhrasesSelectorWizardState {
    return state.phrasesSelectorWizard[wizardId];
}

export const selectPhrasesSelectorHintItems = createCachedSelector(
    selectPhrasesSelectorWizard,
    (state: State) => state.commonData.regions,
    (phrasesSelectorWizard: PhrasesSelectorWizardState, regions: Regions | undefined): HintItem[] => {
        const { groupInfo } = phrasesSelectorWizard;
        const minusPhrases = groupInfo.campaign.type !== CampaignType.CPM_BANNER && {
            label: i18n('minus-phrases-per-group'),
            text: groupInfo.minusKeywords.join(', ') || i18n('not-specified')
        };

        return groupInfo
            ? compact([
                  {
                      label: i18n('campaign'),
                      text: groupInfo.campaign.name
                  },
                  {
                      label: i18n('group'),
                      text: groupInfo.name
                  },
                  {
                      label: i18n('region'),
                      text: regions ? RegionsHelpers.getRegionsTexts(regions, groupInfo.regionIds) : ''
                  },
                  minusPhrases
              ])
            : [];
    }
)(getPhraseWizardCacheKey);

export const selectPhrasesSelectorKeywordStatPhrases = createCachedSelector(
    selectPhrasesSelectorWizard,
    (
        phrasesSelectorWizard: PhrasesSelectorWizardState
    ): {
        similarPhrases: PhrasesSelectorListItem[];
        searchedAlsoPhrases: PhrasesSelectorListItem[];
    } => {
        const { startPhrases, chosenPhrases, keywordsStat } = phrasesSelectorWizard;

        return getKeywordStatPhrases(startPhrases, chosenPhrases, keywordsStat);
    }
)(getPhraseWizardCacheKey);

export const selectPhrasesSelectorPriceContextEnabled = createSelector(
    selectPhrasesSelectorWizard,
    (state: State, wizardId: PhrasesSelectorWizardId) => wizardId,
    (phrasesSelectorWizard: PhrasesSelectorWizardState, wizardId: PhrasesSelectorWizardId): boolean => {
        const {
            groupInfo: { campaign }
        } = phrasesSelectorWizard;

        return Boolean(
            campaign.type === CampaignType.CPM_BANNER &&
                // Кампаниям с автостратегиями ставка не нужна
                !campaign.strategy.isAutoBudget &&
                // На редактировании групп есть своя "ставка"
                wizardId !== PhrasesSelectorWizardId.editGroupsPage
        );
    }
);

export const selectPhrasesSelectorMinusWordsListEnabled = createSelector(
    selectPhrasesSelectorWizard,
    (state: State, wizardId: PhrasesSelectorWizardId, isMinusWordsListEnabled: boolean) => isMinusWordsListEnabled,
    (phrasesSelectorWizard: PhrasesSelectorWizardState, isMinusWordsListEnabled: boolean): boolean => {
        const {
            groupInfo: { campaign }
        } = phrasesSelectorWizard;

        return Boolean(campaign.type !== CampaignType.CPM_BANNER && isMinusWordsListEnabled);
    }
);

export const selectPhrasesSelectorInputData = createCachedSelector(
    selectPhrasesSelectorWizard,
    (state: State, wizardId: PhrasesSelectorWizardId, type: PhrasesSelectorInputColumns) => type,
    (
        phrasesSelectorWizard: PhrasesSelectorWizardState,
        type: PhrasesSelectorInputColumns
    ): {
        value: string;
        error?: string;
    } => {
        return {
            value: phrasesSelectorWizard.inputValue[type],
            error: phrasesSelectorWizard.errors[type]
        };
    }
)(getPhraseWizardCacheKey);
